// Leitor do "Mapa de vendas" do Sage One Facturação.
//
// O relatório traz uma linha por factura e, no fim, uma linha de Total. É essa
// linha que torna a leitura verificável: somam-se as linhas lidas e compara-se
// com o total impresso. Se bater, a leitura está PROVADA — não é confiança, é
// verificação. Se não bater, não se importa nada.
//
// Formato de cada linha (o nome do cliente parte-se às vezes em duas):
//   Cristina 02/01/2026 Fatura de venda FT 2024/839 18,37 0,00 0,00 15,18 3,19 18,37
//                       └ data                       └─────── 6 números ───────┘
//   Produto/Serviço · Descontos · Outros · Total s/IVA · Total IVA · Total
//
// O que interessa é o Total s/IVA — terceiro a contar do fim. É o que é
// tributado, e é a base de tudo na Previsão.

import { extractTextFromPdf } from './parseFatura'

// "1.865,72" → 1865.72 · "-635,52" → -635.52
function numPT(s) {
  if (s == null) return null
  const limpo = String(s).trim().replace(/\./g, '').replace(',', '.')
  const n = Number(limpo)
  return Number.isFinite(n) ? n : null
}

const RE_DATA = /(\d{2})\/(\d{2})\/(\d{4})/
// 6 números seguidos no fim da linha
const RE_SEIS = /(-?[\d.]+,\d{2})\s+(-?[\d.]+,\d{2})\s+(-?[\d.]+,\d{2})\s+(-?[\d.]+,\d{2})\s+(-?[\d.]+,\d{2})\s+(-?[\d.]+,\d{2})\s*$/

export async function parseSageVendas(file) {
  const avisos = []
  const texto = await extractTextFromPdf(file)
  const linhas = texto.split('\n').map(l => l.trim()).filter(Boolean)

  // Período do cabeçalho: "Período 01/01/2026 até 31/08/2026"
  let periodoIni = null, periodoFim = null
  const mPeriodo = texto.match(/Per[ií]odo\s+(\d{2}\/\d{2}\/\d{4})\s+at[ée]\s+(\d{2}\/\d{2}\/\d{4})/i)
  if (mPeriodo) { periodoIni = mPeriodo[1]; periodoFim = mPeriodo[2] }

  const docs = []
  let totalImpresso = null

  // A data e os números nem sempre saem na mesma linha: o tipo do documento
  // ("Fatura de venda") parte-se em duas, e a extracção agrupa por altura, pelo
  // que a data fica numa linha e os valores na seguinte. Guarda-se a última data
  // vista e usa-se quando a linha dos números não traz nenhuma.
  //
  // A data é consumida ao ser usada: assim não pode ser reaproveitada por uma
  // segunda linha de números, que seria inventar um documento que não existe.
  let dataPendente = null

  for (const linha of linhas) {
    const mData = linha.match(RE_DATA)
    if (mData) dataPendente = mData

    const mNums = linha.match(RE_SEIS)
    if (!mNums) continue
    const nums = mNums.slice(1, 7).map(numPT)
    if (nums.some(n => n == null)) continue

    const semIva = nums[3]   // 4.º dos seis = Total s/IVA
    const comIva = nums[5]

    // A linha de Total não traz data própria — é a que serve de conferência.
    // Tem de ser apanhada ANTES de se recorrer à data pendente, senão ficava
    // registada como se fosse mais uma factura.
    if (!mData && /\btotal\b/i.test(linha)) {
      totalImpresso = { semIva, comIva, bruto: nums[0], descontos: nums[1] }
      continue
    }

    const d = mData || dataPendente
    if (!d) continue
    const [, dd, mm, aaaa] = d
    docs.push({
      data: `${aaaa}-${mm}-${dd}`,
      ano: Number(aaaa),
      mes: Number(mm),
      semIva,
      comIva,
      documento: (linha.match(/\b(FT|FR|NC|FS)\s*[\w/]+/i) || [null])[0],
    })
    dataPendente = null
  }

  if (docs.length === 0) {
    // Mostrar como o texto saiu do PDF — sem isto fica-se sem saber se o
    // problema é o ficheiro ou o leitor.
    const amostra = linhas.slice(0, 12).join('\n')
    return {
      ok: false,
      erro: 'Não encontrei nenhuma linha de factura neste PDF. É mesmo o "Mapa de vendas" do Sage?',
      amostra,
      docs: [], meses: [], avisos,
    }
  }

  // Agregar por mês
  const porMes = new Map()
  for (const d of docs) {
    const k = `${d.ano}-${String(d.mes).padStart(2, '0')}`
    const cur = porMes.get(k) || { ano: d.ano, mes: d.mes, semIva: 0, comIva: 0, n: 0 }
    cur.semIva += d.semIva
    cur.comIva += d.comIva
    cur.n += 1
    porMes.set(k, cur)
  }
  const meses = [...porMes.values()]
    .map(m => ({ ...m, semIva: Math.round(m.semIva * 100) / 100, comIva: Math.round(m.comIva * 100) / 100 }))
    .sort((a, b) => a.ano - b.ano || a.mes - b.mes)

  const somaSemIva = Math.round(meses.reduce((s, m) => s + m.semIva, 0) * 100) / 100
  const somaComIva = Math.round(meses.reduce((s, m) => s + m.comIva, 0) * 100) / 100

  // ── A verificação ────────────────────────────────────────────────────────
  // Sem linha de Total não há como provar que se leu tudo. Um documento em que
  // faltasse metade das páginas daria um número menor e igualmente convincente.
  let conferido = false, difSemIva = null
  if (totalImpresso && totalImpresso.semIva != null) {
    difSemIva = Math.round((somaSemIva - totalImpresso.semIva) * 100) / 100
    conferido = Math.abs(difSemIva) < 0.05
    if (!conferido) {
      avisos.push(`A soma das linhas (${somaSemIva.toFixed(2)} €) não bate com o total impresso no relatório (${totalImpresso.semIva.toFixed(2)} €). Diferença de ${difSemIva.toFixed(2)} €.`)
    }
  } else {
    avisos.push('Não encontrei a linha de Total no relatório, por isso não consigo provar que li tudo. Confere os meses um a um antes de importar.')
  }

  const anos = [...new Set(meses.map(m => m.ano))]
  if (anos.length > 1) {
    avisos.push(`O relatório abrange mais do que um ano (${anos.join(', ')}). Só serão importados os meses do ano escolhido.`)
  }

  return {
    ok: true,
    docs, meses, anos,
    periodoIni, periodoFim,
    somaSemIva, somaComIva,
    totalImpresso,
    conferido, difSemIva,
    avisos,
  }
}
